import {
  ApolloClient,
  ApolloLink,
  defaultDataIdFromObject,
  HttpLink,
  InMemoryCache,
} from "@apollo/client";
import { setContext } from "@apollo/client/link/context";
import { onError } from "@apollo/client/link/error";
import { compareVersions } from "compare-versions";
import mixpanel from "mixpanel-browser";
import { uncrunch } from "graphql-crunch";
import decode from "jwt-decode";
import { toast } from "react-toastify";
import { NAMESPACE } from "$/settings/app.json";
import {
  APP,
  AUTH,
  CLIENT_AUTH_REQUEST_TYPE,
  CLIENT_AUTHENTICATION_METHOD,
  CRUNCH,
  JWT,
} from "~/apollo/_config";
import { useGlobalAppVersionStore } from "~/stores/globalAppVersionStore";
import { useGlobalErrorStore } from "~/stores/globalErrorStore";
import { useMaintenanceStore } from "~/stores/maintenanceStore";

const fetcher = (...args) => {
  return window.fetch(...args);
};

const uncruncher = new ApolloLink((operation, forward) =>
  forward(operation).map((response) => {
    response.data = uncrunch(response.data);
    return response;
  }),
);

const { ROUTES: { OEM_LOGIN: LOGIN } = {} } = APP;

// replaced dynamically by vite define
const date = __DATE__;
const id = __APP_ID__;

const version = import.meta.env.PACKAGE_VERSION;

const opts = {
  credentials: "same-origin",
  headers: {
    [`x-${NAMESPACE}-version`]: version,
    [`x-${NAMESPACE}-app`]: id,
    [`x-${NAMESPACE}-built-at`]: date,
    [AUTH.STRATEGIES.CLIENT.AUTH_HEADER]: CLIENT_AUTH_REQUEST_TYPE,
  },
  fetch: fetcher, // because of logrocket
};

const useLocalStorage = CLIENT_AUTHENTICATION_METHOD.LOCAL_STORAGE;

const apolloCache = new InMemoryCache({
  dataIdFromObject: (object) => {
    if (object.uniqueId && object.__typename) {
      return `${object.__typename}:${object.uniqueId}`;
    }
    return defaultDataIdFromObject(object);
  },
});

const endpoint =
  import.meta.env.VITE_APP_GRAPHQL_ENDPOINT ?? APP.ENDPOINT.GRAPHQL;

const uri =
  CRUNCH && CRUNCH.USE ? `${endpoint}/?crunch=${CRUNCH.VERSION}` : endpoint;

const httpLink = new HttpLink({
  uri,
  ...opts,
});

const authMiddlewareLink = setContext(() => {
  const language = JSON.parse(localStorage.getItem("locale") || "{}");
  const headers = {
    headers: {
      "Accept-Language": language?.value || "en",
      [JWT.HEADER.TOKEN.NAME]:
        localStorage.getItem(JWT.LOCAL_STORAGE.TOKEN.NAME) || null,
      [JWT.HEADER.REFRESH_TOKEN.NAME]:
        localStorage.getItem(JWT.LOCAL_STORAGE.REFRESH_TOKEN.NAME) || null, // eslint-disable-line
    },
  };

  if (headers.headers[JWT.HEADER.REFRESH_TOKEN.NAME]) {
    const currentTime = Date.now().valueOf() / 1000;
    const tokenExpiration = decode(
      headers.headers[JWT.HEADER.REFRESH_TOKEN.NAME],
    ).exp;
    if (currentTime > tokenExpiration) {
      if (useLocalStorage) {
        localStorage.removeItem(JWT.LOCAL_STORAGE.TOKEN.NAME);
        localStorage.removeItem(JWT.LOCAL_STORAGE.REFRESH_TOKEN.NAME);
      }
      if (!location.pathname.includes(LOGIN)) location.assign(LOGIN);
    }
  }
  return headers;
});

const setCookieFromToken = (name, value, maxAge = 31536000) => {
  if (!value) return;
  const cookiePath = "; path=/";
  const cookieMaxAge = `; max-age=${maxAge}`;
  const cookieSameSite = "; SameSite=Lax";
  const isSecure = window.location.protocol === "https:";
  const cookieSecure = isSecure ? "; Secure" : "";

  document.cookie = `${name}=${encodeURIComponent(
    value,
  )}${cookiePath}${cookieMaxAge}${cookieSameSite}${cookieSecure}`;
};

// Sent by the backend when an impersonation session has expired but the JWT
// still carries it. Only the client can clear its own localStorage tokens.
const IMPERSONATION_EXPIRED_HEADER = `x-${NAMESPACE}-impersonation-expired`;

const expireCookie = (name) => {
  document.cookie = `${name}=; path=/; max-age=0; SameSite=Lax`;
};

const clearAuth = () => {
  localStorage.clear();
  sessionStorage.clear();

  const mixpanelToken = import.meta.env.VITE_APP_MIXPANEL_TOKEN;
  if (mixpanelToken && import.meta.env.MODE !== "development") {
    mixpanel.unregister("userEmail");
    mixpanel.unregister("userName");
    mixpanel.unregister("oemName");
    mixpanel.unregister("userRole");
    mixpanel.reset();
  }

  client.clearStore().then(() => {
    clearCookies();
    navigate(options?.redirectUrl ?? "/oem");
  });
  expireCookie(JWT.COOKIE.TOKEN.NAME);
  expireCookie(JWT.COOKIE.REFRESH_TOKEN.NAME);
};

const afterwareLink = new ApolloLink((operation, forward) =>
  forward(operation).map((response) => {
    const context = operation.getContext();
    const {
      headers: requestHeaders,
      response: { headers },
    } = context;

    if (headers) {
      // Impersonation session expired: purge our tokens and bounce to login so
      // the user can re-authenticate instead of being stuck sending a stale token.
      if (headers.get(IMPERSONATION_EXPIRED_HEADER)) {
        clearAuth();
        if (!location.pathname.includes(LOGIN)) {
          location.assign(LOGIN);
        }
        return response;
      }

      const token = headers.get(JWT.HEADER.TOKEN.NAME);
      const refreshToken = headers.get(JWT.HEADER.REFRESH_TOKEN.NAME);
      const minimumAppVersionSupported = headers.get(`x-${NAMESPACE}-version`);
      const isAppOldVersion =
        compareVersions(minimumAppVersionSupported, version) === 1;
      useGlobalAppVersionStore.getState().setGlobalAppVersion({
        minimumAppVersionSupported,
        showFlashMessage: isAppOldVersion,
      });

      if (token) {
        localStorage.setItem(JWT.LOCAL_STORAGE.TOKEN.NAME, token);
      }

      if (token || requestHeaders[JWT.HEADER.TOKEN.NAME]) {
        setCookieFromToken(
          JWT.COOKIE.TOKEN.NAME,
          token ?? requestHeaders[JWT.HEADER.TOKEN.NAME],
          6 * 60,
        );
      }

      if (refreshToken) {
        localStorage.setItem(
          JWT.LOCAL_STORAGE.REFRESH_TOKEN.NAME,
          refreshToken,
        );
        setCookieFromToken(
          JWT.COOKIE.REFRESH_TOKEN.NAME,
          refreshToken ?? requestHeaders[JWT.HEADER.REFRESH_TOKEN.NAME],
        );
      }

      if (refreshToken || requestHeaders[JWT.HEADER.REFRESH_TOKEN.NAME]) {
        setCookieFromToken(
          JWT.COOKIE.REFRESH_TOKEN.NAME,
          refreshToken ?? requestHeaders[JWT.HEADER.REFRESH_TOKEN.NAME],
        );
      }
    }
    return response;
  }),
);

const errorLink = onError(
  ({ graphQLErrors = [], networkError = {}, operation }) => {
    const redirect =
      (networkError && networkError.statusCode) ||
      (graphQLErrors && graphQLErrors.length > 0);
    if (redirect) {
      let { statusCode } = networkError;
      if (!statusCode) {
        const errors = graphQLErrors.filter(
          (e) => e.status === 403 || e.status === 401 || e.status === 422,
        );
        const { status = 200 } = errors[0] || {};
        statusCode = status;
      }
      if (statusCode === 401)
        if (
          !location.pathname.includes(LOGIN) &&
          !location.pathname.match("/app/ai/notetaker/[0-9a-f]{24}") &&
          !location.pathname.includes("forgot-password") &&
          !location.pathname.includes("reset-password") &&
          !location.pathname.startsWith("/customer-portal/") &&
          operation?.operationName !== "loginCustomerPortal"
        ) {
          sessionStorage.setItem("searchParams", location.search);
          location.assign(
            `${LOGIN}?redirect=${encodeURIComponent(location.pathname)}`,
          );
        }
      if (statusCode === 403) {
        console.error("Network Error: ", networkError);
        console.error("GraphQLErrors:", graphQLErrors);
        useGlobalErrorStore
          .getState()
          .setGlobalError({ isErrorAlertOpen: true });
      }

      if (networkError?.statusCode === 422) {
        const transformedErrors = graphQLErrors?.reduce((obj, error) => {
          if (error?.status !== 422) return obj;
          return {
            ...obj,
            ...error.extensions.fields,
          };
        }, {});
        networkError.message = transformedErrors;
        networkError.graphQLErrors = graphQLErrors;
        if (
          !networkError.message ||
          !Object.keys(transformedErrors || {}).length
        )
          toast.error("Oops, something went wrong!");
      }

      if (statusCode === 503) {
        useMaintenanceStore
          .getState()
          .setMaintenance({ isMaintenanceOn: true });
        return;
      }

      if (statusCode === 504) {
        return;
      }

      if (statusCode >= 500) {
        console.error("Network Error: ", networkError);
        console.error("GraphQLErrors:", graphQLErrors);
        useGlobalErrorStore
          .getState()
          .setGlobalError({ isErrorAlertOpen: true });
      }
    }
  },
);

let links = [
  errorLink,
  CRUNCH && CRUNCH.USE ? ApolloLink.concat(uncruncher, httpLink) : httpLink,
];

if (useLocalStorage) {
  links = [
    errorLink,
    afterwareLink,
    authMiddlewareLink,
    CRUNCH && CRUNCH.USE ? ApolloLink.concat(uncruncher, httpLink) : httpLink,
  ];
}

const link = ApolloLink.from(links);

const client = new ApolloClient({
  link,
  cache: apolloCache,
  connectToDevTools: true,
});

if (useLocalStorage) {
  let currentToken = window.localStorage.getItem(JWT.LOCAL_STORAGE.TOKEN.NAME);

  window.onstorage = (event) => {
    if (event.key !== JWT.LOCAL_STORAGE.TOKEN.NAME) return;

    const newToken = window.localStorage.getItem(JWT.LOCAL_STORAGE.TOKEN.NAME);

    if (currentToken && !newToken) {
      // someone in another tab logged out while I am logged in here
      currentToken = newToken;
      client.clearStore().then(() => location.replace(LOGIN));
    } else if (!currentToken && newToken) {
      // I am not logged in, but someone else just logged in in another tab
      currentToken = newToken;
      window.location.reload();
    } else if (currentToken && newToken && currentToken !== newToken) {
      // I am logged in and received a new token in local storage
      const currentTokenData = decode(currentToken);
      const newTokenData = decode(newToken);
      if (currentTokenData?.user?.id !== newTokenData?.user?.id) {
        // id(s) are different, thus someone in another tab logged in with different user while I was logged in with another user here
        // thus refresh the page to sync the user and view the new data
        window.location.reload();
      }
      // otherwise, I am logged in and received a new token in local storage
      // but it's the same user, so I don't need to do anything
      // it's standard behavior for refreshing tokens
    }
  };
}

// TODO: [rico] - handle the behavior when strategy is HTTP_ONLY

export default client;
